/**
 * Transport for the chat widget → `POST /chat` on the marketbrewer Lambda API.
 *
 * Same base URL and deadline discipline as ContactForm.tsx. Response handling follows the
 * contract the N&S forms learned the hard way: a 2xx whose body says `success: false` is a
 * failure, and a hung connection must end in an error the visitor can retry.
 */

import { isTimeoutError, timeoutSignal } from "@/lib/timeoutSignal";

const FALLBACK_API_URL = "https://wcvi65m780.execute-api.us-east-1.amazonaws.com/prod";
const apiBase = (): string => process.env.API_BASE_URL || FALLBACK_API_URL;

const CHAT_TIMEOUT_MS = 25_000;
const FORM_TIMEOUT_MS = 15_000;

export type Failure = "timeout" | "network" | "http" | "rejected";

type Json = Record<string, unknown>;

interface Transport {
  ok: boolean;
  status: number;
  body: Json | null;
  failure?: Failure;
}

async function post(path: string, payload: Json, timeoutMs: number): Promise<Transport> {
  try {
    const response = await fetch(`${apiBase()}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: timeoutSignal(timeoutMs),
    });
    const body = (await response.json().catch(() => null)) as Json | null;
    if (!response.ok) return { ok: false, status: response.status, body, failure: "http" };
    if (body && body.success === false) return { ok: false, status: response.status, body, failure: "rejected" };
    return { ok: true, status: response.status, body };
  } catch (error) {
    if (isTimeoutError(error)) return { ok: false, status: 0, body: null, failure: "timeout" };
    return { ok: false, status: 0, body: null, failure: "network" };
  }
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

export type StartResult =
  | { ok: true; conversationId: string; mode: "ai" | "form"; turnsLeft: number }
  | { ok: false; failure: Failure; status: number };

export async function startChat(input: {
  language: "en" | "es";
  page: string;
  renderTime: number;
  hp: string;
}): Promise<StartResult> {
  const r = await post(
    "/chat",
    { action: "start", language: input.language, page: input.page, renderTime: String(input.renderTime), hp: input.hp },
    FORM_TIMEOUT_MS,
  );
  const id = str(r.body?.conversationId);
  if (!r.ok || !id) return { ok: false, failure: r.failure ?? "http", status: r.status };
  return {
    ok: true,
    conversationId: id,
    mode: r.body?.mode === "form" ? "form" : "ai",
    turnsLeft: num(r.body?.turnsLeft, 8),
  };
}

export type MessageResult =
  | { ok: true; mode: "ai"; reply: string; turnsLeft: number }
  | { ok: true; mode: "form"; reason: string; turnsLeft: number }
  | { ok: false; failure: Failure; status: number; error: string | null };

export async function sendChatMessage(input: { conversationId: string; text: string }): Promise<MessageResult> {
  const r = await post("/chat", { action: "message", conversationId: input.conversationId, text: input.text }, CHAT_TIMEOUT_MS);
  if (!r.ok) {
    return { ok: false, failure: r.failure ?? "http", status: r.status, error: str(r.body?.error) || null };
  }
  const turnsLeft = num(r.body?.turnsLeft, 0);
  if (r.body?.mode === "ai" && str(r.body?.reply)) {
    return { ok: true, mode: "ai", reply: str(r.body?.reply), turnsLeft };
  }
  return { ok: true, mode: "form", reason: str(r.body?.reason) || "form", turnsLeft };
}

export interface TranscriptTurn {
  role: "visitor" | "assistant";
  text: string;
}

export interface LeadInput {
  conversationId: string | null;
  language: "en" | "es";
  name: string;
  email: string;
  phone: string;
  business: string;
  message: string;
  hp: string;
  /** What the visitor and assistant said, oldest first, for the fallback path only. */
  transcript: TranscriptTurn[];
}

/** `fallbackFrom` says why the lead went through the contact form: the /chat status, or "no_conversation". */
export type LeadResult =
  | { ok: true; via: "chat" | "contact-form"; fallbackFrom?: string }
  | { ok: false; failure: Failure; status: number; error: string | null; fallbackFrom?: string };

/**
 * Whether a failed `/chat` lead is worth sending through the contact form instead. Anything that
 * says "the chat service is not there right now" qualifies; a 400 means the visitor's details were
 * refused, and the contact form would refuse them too, so that one goes back to the card. A 413 is
 * a note too long for the chat Lambda's body limit: the fallback trims it to fit, so it goes there
 * instead of failing the same way on every retry.
 */
export function shouldFallBack(status: number): boolean {
  return status === 0 || status === 404 || status === 408 || status === 413 || status === 429 || status >= 500;
}

// ---- the contact-form fallback payload ----------------------------------------------------

/**
 * What `/form-submission` accepts, from the DEPLOYED contact-form bundle (not its repo source,
 * which has drifted): message 10–5000 chars, phone `^[+]?[0-9\-\s()]{10,30}$`, name ≤ 100. The
 * repo source, which may be deployed later, also HTML-escapes every field BEFORE those checks
 * and refuses bodies over 10,240 characters — so the budgets below hold under both versions,
 * with room to spare.
 */
export const FALLBACK_LIMITS = {
  messageEscaped: 4_500,
  bodyBytes: 9_000,
  nameEscaped: 100,
  business: 200,
} as const;

const CONTACT_FORM_PHONE_RE = /^[+]?[0-9\-\s()]{10,30}$/;

/** Mirrors the contact form's escape, so lengths are measured the way its validator will see them. */
export const escapedLength = (s: string): number => s.replace(/&/g, "&amp;").replace(/[<>]/g, "&lt;").replace(/["']/g, "&#039;").length;

const utf8Bytes = (s: string): number => new TextEncoder().encode(s).length;

/** Cuts on whole characters (never half an emoji) until the escaped form fits. */
function fitEscaped(s: string, max: number): string {
  if (escapedLength(s) <= max) return s;
  const chars = Array.from(s);
  let lo = 0;
  let hi = chars.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (escapedLength(`${chars.slice(0, mid).join("")}…`) <= max) lo = mid;
    else hi = mid - 1;
  }
  return `${chars.slice(0, lo).join("")}…`;
}

/**
 * The chat accepts "703.555.0100" and seven-digit numbers; the contact form does not. Dots become
 * dashes; anything still refused is sent as no phone, and the caller keeps the raw value in the note.
 */
export function normalizePhone(raw: string): { phone: string; kept: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) return { phone: "", kept: null };
  const dashed = trimmed.replace(/\./g, "-").replace(/\s+/g, " ");
  if (CONTACT_FORM_PHONE_RE.test(dashed)) return { phone: dashed, kept: null };
  return { phone: "", kept: trimmed };
}

// The contact form silently drops (200, "filtered") any message containing one of these. That
// filter is aimed at cold outreach, and this visitor already came through the chat, so every
// match in the message (visitor, assistant, phone as typed) gets a soft hyphen: a visitor asking
// about email deliverability must not make a real lead vanish.
const CONTACT_FORM_SPAM_KEYWORDS = [
  "contact form outreach",
  "deliverability",
  "smartblasts",
  "wikipedia page",
  "i help businesses book meetings",
  "my private network",
  "reply stop to opt out",
];

function defuseSpamKeywords(text: string): string {
  let out = text;
  for (const kw of CONTACT_FORM_SPAM_KEYWORDS) {
    const re = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    out = out.replace(re, (m) => `${m.slice(0, 1)}­${m.slice(1)}`);
  }
  return out;
}

const TAG_LINE = "[Sent from the website chat widget — chat service unavailable]";

function transcriptLine(turn: TranscriptTurn): string {
  return `${turn.role === "visitor" ? "Visitor" : "Assistant"}: ${turn.text}`;
}

/**
 * The `/form-submission` body for a chat lead. Pure, so the contract test can run it through the
 * contact form's own rules. The transcript is trimmed from the OLDEST turns: the latest exchange is
 * what Jorge needs to reply to, and the conversation id lets him pull the rest from the chat table.
 * The visitor's words come first and the widget's own English lines last, because the contact form
 * quotes the first 200 characters back to the visitor as "what you sent".
 */
export function buildFallbackPayload(input: LeadInput): Record<string, string> {
  const { phone, kept } = normalizePhone(input.phone);
  const head = [
    TAG_LINE,
    input.conversationId ? `Chat: ${input.conversationId}` : null,
    kept ? `Phone as typed: ${kept}` : null,
  ].filter((l): l is string => l !== null);
  const note = input.message.trim();
  const turns = input.transcript.filter((t) => t.text.trim());

  const compose = (visitorNote: string, keep: number): string => {
    const shown = turns.slice(turns.length - keep);
    const dropped = turns.length - keep;
    const lines: string[] = visitorNote ? [visitorNote] : [];
    if (shown.length > 0) {
      lines.push(`${lines.length ? "\n" : ""}Conversation so far:`);
      if (dropped > 0) lines.push(`(${dropped} earlier ${dropped === 1 ? "line" : "lines"} left out)`);
      lines.push(...shown.map(transcriptLine));
    }
    lines.push(`${lines.length ? "\n" : ""}${head.join("\n")}`);
    return defuseSpamKeywords(lines.join("\n"));
  };

  const payloadFor = (message: string): Record<string, string> => ({
    name: fitEscaped(input.name, FALLBACK_LIMITS.nameEscaped),
    email: input.email,
    phone,
    message,
    subject: `Chat widget - ${input.language === "es" ? "Español" : "English"}`,
    website: input.hp,
    businessName: Array.from(input.business).slice(0, FALLBACK_LIMITS.business).join(""),
    websiteUrl: "",
    dmvLocal: "",
    language: input.language,
    renderTime: "",
  });

  const fits = (p: Record<string, string>): boolean =>
    escapedLength(p.message ?? "") <= FALLBACK_LIMITS.messageEscaped && utf8Bytes(JSON.stringify(p)) <= FALLBACK_LIMITS.bodyBytes;

  for (let keep = turns.length; keep >= 0; keep--) {
    const p = payloadFor(compose(note, keep));
    if (fits(p)) return p;
  }
  // Even with no transcript the visitor's own note is too long: keep its start. Halving the budget
  // leaves room for the byte limit, since escaping and multi-byte characters both grow it.
  const headOnly = compose("", 0);
  let budget = FALLBACK_LIMITS.messageEscaped - escapedLength(headOnly) - 2;
  for (;;) {
    const p = payloadFor(compose(fitEscaped(note, Math.max(budget, 20)), 0));
    if (fits(p) || budget <= 20) return p;
    budget = Math.floor(budget / 2);
  }
}

/**
 * Saves the visitor's details. With a live conversation this goes to `/chat`; when that fails
 * for any reason other than the details themselves (see shouldFallBack), or there is no
 * conversation, it goes to the contact-form endpoint every other form on the site uses, so a
 * lead is never lost to the chat service being down.
 */
export async function submitChatLead(input: LeadInput): Promise<LeadResult> {
  let fallbackFrom = "no_conversation";
  if (input.conversationId) {
    const r = await post(
      "/chat",
      {
        action: "lead",
        conversationId: input.conversationId,
        name: input.name,
        email: input.email,
        phone: input.phone,
        business: input.business,
        message: input.message,
        hp: input.hp,
      },
      FORM_TIMEOUT_MS,
    );
    if (r.ok) return { ok: true, via: "chat" };
    if (!shouldFallBack(r.status)) return { ok: false, failure: r.failure ?? "http", status: r.status, error: str(r.body?.error) || null };
    fallbackFrom = String(r.status);
  }

  const r = await post("/form-submission", buildFallbackPayload(input), FORM_TIMEOUT_MS);
  if (r.ok) return { ok: true, via: "contact-form", fallbackFrom };
  return { ok: false, failure: r.failure ?? "http", status: r.status, error: str(r.body?.error) || null, fallbackFrom };
}

/** Fire-and-forget: lets the Lambda write its summary when a real conversation ends. */
export function endChat(conversationId: string): void {
  const payload = JSON.stringify({ action: "end", conversationId });
  const url = `${apiBase()}/chat`;
  // sendBeacon survives tab close and cannot set Content-Type: application/json on all
  // browsers; the Lambda parses the body regardless of the header, and the beacon carries
  // no CORS preflight for text/plain, which is exactly what makes it reliable on unload.
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      if (navigator.sendBeacon(url, new Blob([payload], { type: "text/plain" }))) return;
    } catch {
      // fall through to fetch
    }
  }
  void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {
    /* best effort */
  });
}
